import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingCart, ArrowRight, Trash2, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProducts } from '../hooks/useSanityProducts';
import UpsellOffer from './UpsellOffer';
import { displayBookName, isIndexedBook, bookPlatform } from '../utils/bookGrouping';

function _bookPlatform(p) { return p && isIndexedBook(p) ? bookPlatform(p) : null; }

// Quick-add strip: 4-6 popular accessories the shopper doesn't already have.
// Products with REQUIRED variants can't be quick-added (they need a variant
// pick) — clicking them navigates to the full product page instead.
var QUICK_ADD_SLUGS = [
  'muteclip', 'belloflex-mute', 'brass-plank', 'fingerflow',
  'practice-mute', 'mutecap', 'brassblast', 'trumpet-o-rings',
  'saxolift', 'real-book-vol-1-bb'
];

// Inline-price reformatter — same helper logic as VariantSelectors. Variant
// labels stored on cart items contain literal "+$9.99" tokens that need to
// be re-rendered in the shopper's selected currency so the cart line below
// the product name shows e.g. "+8.07 CHF" instead of "+$9.99" next to the
// already-converted item price.
function _reformatPricesInLabel(label, formatPrice) {
  if (!label || typeof label !== 'string' || typeof formatPrice !== 'function') return label;
  return label.replace(/\+?\$\s?([0-9]+(?:,[0-9]{3})*(?:\.[0-9]+)?)/g, function (m, num) {
    var plus = m.charAt(0) === '+';
    var v = parseFloat(num.replace(/,/g, ''));
    if (!isFinite(v)) return m;
    try { var s = formatPrice(v); return plus ? '+' + s : s; } catch (_e) { return m; }
  });
}

function CartDrawer() {
  var cart = useCart();
  var ref = useTranslation();
  var formatPrice = ref.formatPrice;
  var navigate = useNavigate();
  var sanityRef = useSanityProducts();
  var allProducts = sanityRef.products || [];

  if (!cart.isOpen) return null;

  var itemEls = [];
  for (var i = 0; i < cart.items.length; i++) {
    var item = cart.items[i];
    itemEls.push(React.createElement(CartItem, { key: item.key, item: item, formatPrice: formatPrice, onRemove: cart.removeItem, onUpdateQty: cart.updateQty }));
  }

  function handleCheckout() {
    cart.setIsOpen(false);
    navigate('/checkout');
  }

  return React.createElement('div', { 'data-testid': 'cart-drawer', className: 'fixed inset-0 z-50' },
    React.createElement('div', { className: 'absolute inset-0 bg-black/60 backdrop-blur-sm', onClick: function() { cart.setIsOpen(false); } }),
    React.createElement('div', { className: 'absolute right-0 top-0 h-full w-full max-w-md bg-neutral-950 border-l border-white/10 shadow-2xl flex flex-col' },
      React.createElement('div', { className: 'flex items-center justify-between p-6 border-b border-white/10' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(ShoppingCart, { className: 'w-5 h-5 text-[#d4af37]' }),
          React.createElement('h2', { className: 'text-xl font-bold text-white' }, 'Cart'),
          React.createElement('span', { className: 'px-2 py-0.5 rounded-full bg-[#d4af37] text-black text-xs font-bold' }, cart.totalItems)
        ),
        React.createElement('button', { onClick: function() { cart.setIsOpen(false); }, className: 'p-2 hover:bg-white/5 rounded-lg transition-colors' },
          React.createElement(X, { className: 'w-5 h-5 text-neutral-400' })
        )
      ),
      React.createElement('div', { className: 'flex-1 overflow-y-auto p-6' },
        cart.items.length === 0
          ? React.createElement('div', { className: 'text-center py-16' },
              React.createElement(ShoppingCart, { className: 'w-12 h-12 text-neutral-700 mx-auto mb-4' }),
              React.createElement('p', { className: 'text-neutral-500' }, 'Your cart is empty')
            )
          : React.createElement('div', null,
              React.createElement('div', { className: 'space-y-4' }, itemEls),
              React.createElement(UpsellOffer, {
                cartItems: cart.items,
                allProducts: allProducts,
                addToCart: cart.addItem,
                variant: 'hero'
              }),
              React.createElement(QuickAddStrip, {
                cartItems: cart.items,
                allProducts: allProducts,
                addToCart: cart.addItem,
                closeDrawer: function() { cart.setIsOpen(false); },
                formatPrice: formatPrice,
                navigate: navigate
              })
            )
      ),
      cart.items.length > 0 ? React.createElement('div', { className: 'p-6 border-t border-white/10' },
        React.createElement('button', {
          onClick: handleCheckout,
          'data-testid': 'cart-checkout-btn',
          className: 'w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-4 rounded-full hover:bg-white transition-all text-lg'
        },
          'Checkout',
          React.createElement(ArrowRight, { className: 'w-5 h-5' })
        ),
        React.createElement('button', {
          onClick: function() { cart.setIsOpen(false); navigate('/shop'); },
          'data-testid': 'cart-continue-shopping-btn',
          className: 'w-full flex items-center justify-center gap-2 text-neutral-300 hover:text-white font-medium py-3 mt-2 rounded-full border border-white/10 hover:border-white/30 transition-all text-sm'
        },
          React.createElement(ArrowLeft, { className: 'w-4 h-4' }),
          'Continue shopping'
        ),
        React.createElement('p', { className: 'text-neutral-600 text-xs text-center mt-3' }, 'Secure payment — Card, PayPal, Apple Pay & Google Pay')
      ) : null
    )
  );
}

function CartItem(props) {
  var item = props.item;
  var formatPrice = props.formatPrice;
  var img = item.variantImage || item.product.image || (item.product.images && item.product.images[0]) || '';

  return React.createElement('div', { className: 'flex gap-4 p-4 bg-neutral-900 rounded-xl border border-white/5' },
    React.createElement('img', { src: img, alt: item.product.name, className: 'w-20 h-20 rounded-lg object-cover flex-shrink-0' }),
    React.createElement('div', { className: 'flex-1 min-w-0' },
      React.createElement('h4', { className: 'font-medium text-sm truncate ' + (_bookPlatform(item.product) === 'mobilesheets' ? 'text-emerald-400' : _bookPlatform(item.product) === 'forscore' ? 'text-sky-400' : 'text-white') },
        _bookPlatform(item.product) ? displayBookName(item.product) : item.product.name
      ),
      _bookPlatform(item.product) ? React.createElement('p', {
        className: 'text-[10px] font-bold uppercase tracking-wider ' + (_bookPlatform(item.product) === 'mobilesheets' ? 'text-emerald-400/80' : 'text-sky-400/80')
      }, '(', _bookPlatform(item.product) === 'mobilesheets' ? 'MobileSheets' : 'forScore', ')') : null,
      item.variant ? React.createElement('p', { className: 'text-neutral-500 text-xs mt-0.5' }, _reformatPricesInLabel(item.variant, formatPrice)) : null,
      React.createElement('p', { className: 'text-[#d4af37] font-semibold text-sm mt-1' }, formatPrice(item.unitPrice || item.product.price)),
      React.createElement('div', { className: 'flex items-center gap-3 mt-2' },
        // A digital file is bought once — no quantity stepper, nothing to double.
        item.isDigital ? React.createElement('span', { className: 'text-neutral-500 text-xs', 'data-testid': 'cart-digital-note-' + item.product.slug },
          'Digital file \u00b7 one per order'
        ) : React.createElement(React.Fragment, null,
          React.createElement('button', { onClick: function() { props.onUpdateQty(item.key, item.qty - 1); }, 'data-testid': 'cart-qty-minus-' + item.product.slug, className: 'w-7 h-7 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-neutral-700' },
            React.createElement(Minus, { className: 'w-3 h-3 text-white' })
          ),
          React.createElement('span', { className: 'text-white text-sm font-medium' }, item.qty),
          React.createElement('button', { onClick: function() { props.onUpdateQty(item.key, item.qty + 1); }, 'data-testid': 'cart-qty-plus-' + item.product.slug, className: 'w-7 h-7 rounded-full bg-neutral-800 flex items-center justify-center hover:bg-neutral-700' },
            React.createElement(Plus, { className: 'w-3 h-3 text-white' })
          )
        ),
        React.createElement('button', { onClick: function() { props.onRemove(item.key); }, 'data-testid': 'cart-remove-' + item.product.slug, 'aria-label': 'Remove', className: 'ml-auto text-neutral-600 hover:text-red-400 flex items-center gap-1 text-xs' },
          React.createElement(Trash2, { className: 'w-3.5 h-3.5' }),
          'Remove'
        )
      )
    )
  );
}

// QuickAddStrip — horizontal scroll of 4-6 popular products the shopper
// doesn't already have. Simple items add directly to the cart with one tap;
// products with a REQUIRED variant navigate to the full product page so the
// shopper can pick their option first.
function QuickAddStrip(props) {
  var cartItems = props.cartItems;
  var allProducts = props.allProducts;
  var addToCart = props.addToCart;
  var closeDrawer = props.closeDrawer;
  var formatPrice = props.formatPrice;
  var navigate = props.navigate;

  if (!allProducts || allProducts.length === 0) return null;

  // Slugs already in the cart — skip them so we never suggest a duplicate.
  var inCart = {};
  for (var a = 0; a < cartItems.length; a++) {
    if (cartItems[a] && cartItems[a].product && cartItems[a].product.slug) {
      inCart[cartItems[a].product.slug] = true;
    }
  }

  // Build a slug→product index for O(1) lookup.
  var bySlug = {};
  for (var b = 0; b < allProducts.length; b++) {
    if (allProducts[b] && allProducts[b].slug) bySlug[allProducts[b].slug] = allProducts[b];
  }

  var suggestions = [];
  for (var c = 0; c < QUICK_ADD_SLUGS.length && suggestions.length < 6; c++) {
    var slug = QUICK_ADD_SLUGS[c];
    if (inCart[slug]) continue;
    var p = bySlug[slug];
    if (!p) continue;
    suggestions.push(p);
  }
  if (suggestions.length === 0) return null;

  function hasRequiredVariants(p) {
    var v = p && p.variants;
    if (!v) return false;
    if (Array.isArray(v.groups) && v.groups.length > 0) return true;
    if (Array.isArray(v.options) && v.options.length > 0 && v.required !== false) return true;
    return false;
  }

  function pickHandler(p) {
    return function () {
      if (hasRequiredVariants(p)) {
        // Needs a variant selection — send them to the product page.
        closeDrawer();
        navigate('/product/' + p.slug);
      } else {
        // Simple item — add straight to the cart.
        addToCart(p, null, {
          isDigital: (p.category === 'digital' || p.category === 'sheet-music'),
          unitPrice: p.price
        });
      }
    };
  }

  var cards = suggestions.map(function (p) {
    var img = p.image || (p.images && p.images[0]) || '';
    return React.createElement('button', {
      key: p.slug,
      onClick: pickHandler(p),
      'data-testid': 'cart-quickadd-' + p.slug,
      className: 'flex-shrink-0 w-36 bg-neutral-900 border border-white/10 rounded-xl overflow-hidden hover:border-[#d4af37]/60 hover:bg-neutral-800 transition-all text-left group'
    },
      React.createElement('div', { className: 'aspect-square bg-neutral-950 overflow-hidden' },
        img ? React.createElement('img', {
          src: img, alt: p.name, loading: 'lazy',
          className: 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
        }) : null
      ),
      React.createElement('div', { className: 'p-2.5' },
        React.createElement('div', { className: 'text-white text-xs font-medium leading-tight line-clamp-2 min-h-[2rem]' }, p.name),
        React.createElement('div', { className: 'flex items-center justify-between mt-2' },
          React.createElement('span', { className: 'text-[#d4af37] text-sm font-bold' }, formatPrice(p.price)),
          React.createElement('span', {
            'aria-hidden': true,
            className: 'w-6 h-6 rounded-full bg-[#d4af37]/15 text-[#d4af37] flex items-center justify-center group-hover:bg-[#d4af37] group-hover:text-black transition-colors'
          }, React.createElement(Plus, { className: 'w-3.5 h-3.5' }))
        )
      )
    );
  });

  return React.createElement('div', { 'data-testid': 'cart-quickadd-strip', className: 'mt-6' },
    React.createElement('div', { className: 'flex items-center justify-between mb-3 px-1' },
      React.createElement('p', { className: 'text-neutral-400 text-xs font-semibold uppercase tracking-wider' }, 'More you might like'),
      React.createElement('button', {
        onClick: function () { closeDrawer(); navigate('/shop'); },
        'data-testid': 'cart-quickadd-see-all',
        className: 'text-[#d4af37] hover:text-white text-xs font-semibold'
      }, 'See all →')
    ),
    React.createElement('div', { className: 'flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 snap-x snap-mandatory' }, cards)
  );
}


export default CartDrawer;
